import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSensor, useSensors, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import type { UniqueIdentifier, DragStartEvent, DragOverEvent, DragEndEvent } from '@dnd-kit/core';
import { fetchWithAuth } from '@/lib/api';
import { dispatchHandoffToCloud } from '@/lib/cloud-dispatch';
import { toast } from 'sonner';
import { usePanelSearch } from '@/hooks/use-panel-search';
import { useWorkspace } from '@/providers/workspace-provider';
import type { View, SidePanelView, RoadmapViewMode, RoadmapItem, Section, SectionColor } from './product-board-types';
import {
  loadProductBoardUiFromStorage,
  saveProductBoardUiToStorage,
  emptyRoadmapSections,
  mockRoadmapSectionsFallback,
  mockData,
  normalizeBetDocFilePath,
  mergeActiveDispatchesIntoResult,
  type CloudDispatchResultEntry,
  generateItemPreviewMarkdown,
  getBetMarkdownMock,
  roadmapItemMatchesSearch,
  roadmapItemMatchesBoardFilters,
  sortedBoardTagFilterEntries,
  sortedCycleSelectRowsFromSections,
  countItemsWithMvpCriticalTagInSections,
  countDispatchableNowFilterItemsInSections,
  betDocumentReferenceId,
  type ProductBoardFilters,
  type BoardCycleFilter,
} from './product-board.utils';
import { resolveCloudDispatchTargets } from './bet-handoff-path';

const emitNotification = (_: unknown) => {};

/** Scroll the bet card with the given ID into view after the next paint. */
function scrollBetCardIntoView(betId: string) {
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-bet-id="${betId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  });
}

interface UseProductBoardStateProps {
  documentId?: string;
  documentFilePath?: string;
}

export function useProductBoardState({ documentId, documentFilePath }: UseProductBoardStateProps) {
  const { activeWorkspaceIds } = useWorkspace();
  const delegateMcpOrganizationId = activeWorkspaceIds[0] ?? null;
  const storedUi = loadProductBoardUiFromStorage();
  const [currentView, setCurrentView] = useState<View>(() => {
    const v = storedUi?.currentView;
    if (v === 'dashboard' || v === 'roadmap' || v === 'status-board' || v === 'cycle-detail') return v;
    return 'dashboard';
  });
  const { searchOpen, searchQuery, setSearchQuery, inputRef, close, open } = usePanelSearch({
    enabled: currentView === 'roadmap',
  });

  const [selectedBetId, setSelectedBetId] = useState<string | null>(null);
  const [sidePanelView, setSidePanelView] = useState<SidePanelView>(() => {
    const sv = storedUi?.sidePanelView;
    if (sv === 'cycle-detail') return 'cycle-detail';
    if (sv === 'bet-preview' && storedUi?.sidePanelBetId) return 'bet-preview';
    if (sv === 'roadmap-doc-preview' && storedUi?.sidePanelRoadmapPath) return 'roadmap-doc-preview';
    return null;
  });
  const [sidePanelBetId, setSidePanelBetId] = useState<string | null>(() =>
    storedUi?.sidePanelView === 'bet-preview' && storedUi?.sidePanelBetId ? storedUi.sidePanelBetId : null
  );
  const [sidePanelRoadmapPath, setSidePanelRoadmapPath] = useState<string | null>(() =>
    storedUi?.sidePanelView === 'roadmap-doc-preview' && storedUi?.sidePanelRoadmapPath
      ? storedUi.sidePanelRoadmapPath
      : null
  );
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(
    () => storedUi?.filtersExpanded === true
  );
  const [betPreviewMarkdown, setBetPreviewMarkdown] = useState<string | null>(null);
  const [betPreviewLoading, setBetPreviewLoading] = useState(false);
  const [betPreviewError, setBetPreviewError] = useState<string | null>(null);

  const [cloudDispatchingId, setCloudDispatchingId] = useState<string | null>(null);
  const [cloudDispatchResult, setCloudDispatchResult] = useState<Record<string, CloudDispatchResultEntry>>({});

  const [roadmapViewMode, setRoadmapViewMode] = useState<RoadmapViewMode>('columns');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  
  const [sections, setSections] = useState<Section[]>(emptyRoadmapSections);
  const [boardTitle, setBoardTitle] = useState<string | null>(null);

  /** Sections with items filtered to only those matching the current search query. */
  const searchFilteredSections = useMemo(() => {
    if (!searchOpen || !searchQuery.trim()) return sections;
    return sections.map(section => ({
      ...section,
      items: section.items.filter(item => roadmapItemMatchesSearch(item, searchQuery)),
    }));
  }, [sections, searchOpen, searchQuery]);

  const isSearchActive = searchOpen && !!searchQuery.trim();

  const betSearchDim = useCallback(
    (item: RoadmapItem) =>
      searchOpen && !!searchQuery.trim() && !roadmapItemMatchesSearch(item, searchQuery),
    [searchOpen, searchQuery]
  );

  type BoardFiltersState = {
    mvpOnly: boolean;
    dispatchOnly: boolean;
    tagConjunction: string[];
    cycle: BoardCycleFilter;
  };

  const [boardFiltersState, setBoardFiltersState] = useState<BoardFiltersState>({
    mvpOnly: false,
    dispatchOnly: false,
    tagConjunction: [],
    cycle: 'all',
  });

  const filtersUrlHydratedRef = useRef(false);

  const boardFilters = useMemo<ProductBoardFilters>(
    () => ({
      tagConjunction: boardFiltersState.tagConjunction,
      mvpCriticalOnly: boardFiltersState.mvpOnly,
      dispatchableNowOnly: boardFiltersState.dispatchOnly,
      cycle: boardFiltersState.cycle,
    }),
    [boardFiltersState]
  );

  const isBoardFilterActive = useMemo(
    () =>
      boardFiltersState.mvpOnly ||
      boardFiltersState.dispatchOnly ||
      boardFiltersState.tagConjunction.length > 0 ||
      boardFiltersState.cycle !== 'all',
    [boardFiltersState]
  );

  const displaySections = useMemo(() => {
    if (!isBoardFilterActive) return searchFilteredSections;
    return searchFilteredSections.map((section) => ({
      ...section,
      items: section.items.filter((item) => roadmapItemMatchesBoardFilters(item, boardFilters)),
    }));
  }, [searchFilteredSections, boardFilters, isBoardFilterActive]);

  // Board filter counts and tag/cycle options use the same scope as board filters: all columns
  // after the roadmap panel search is applied (searchFilteredSections).
  const boardTagFilterEntries = useMemo(
    () => sortedBoardTagFilterEntries(searchFilteredSections),
    [searchFilteredSections]
  );
  const boardCycleSelectRows = useMemo(
    () => sortedCycleSelectRowsFromSections(searchFilteredSections),
    [searchFilteredSections]
  );
  const boardFilterMvpCount = useMemo(
    () => countItemsWithMvpCriticalTagInSections(searchFilteredSections),
    [searchFilteredSections]
  );
  const boardFilterDispatchCount = useMemo(
    () => countDispatchableNowFilterItemsInSections(searchFilteredSections),
    [searchFilteredSections]
  );

  const syncBoardFiltersToUrl = useCallback((f: ProductBoardFilters) => {
    try {
      const url = new URL(window.location.href);
      if (f.mvpCriticalOnly) url.searchParams.set('pbMvp', '1');
      else url.searchParams.delete('pbMvp');
      if (f.dispatchableNowOnly) url.searchParams.set('pbDispatch', '1');
      else url.searchParams.delete('pbDispatch');
      if (f.tagConjunction.length) url.searchParams.set('pbTags', f.tagConjunction.join(','));
      else url.searchParams.delete('pbTags');
      if (f.cycle === 'all') url.searchParams.delete('pbCycle');
      else if (f.cycle === 'none') url.searchParams.set('pbCycle', 'none');
      else url.searchParams.set('pbCycle', String(f.cycle));
      window.history.replaceState({}, '', url);
    } catch {
      /* ignore */
    }
  }, []);

  const applyBoardFilters = useCallback(
    (mutate: (prev: BoardFiltersState) => BoardFiltersState) => {
      setBoardFiltersState((prev) => {
        const next = mutate(prev);
        syncBoardFiltersToUrl({
          tagConjunction: next.tagConjunction,
          mvpCriticalOnly: next.mvpOnly,
          dispatchableNowOnly: next.dispatchOnly,
          cycle: next.cycle,
        });
        return next;
      });
    },
    [syncBoardFiltersToUrl]
  );

  const toggleBoardFilterMvp = useCallback(() => {
    applyBoardFilters((prev) => ({ ...prev, mvpOnly: !prev.mvpOnly }));
  }, [applyBoardFilters]);

  const toggleBoardFilterDispatch = useCallback(() => {
    applyBoardFilters((prev) => ({ ...prev, dispatchOnly: !prev.dispatchOnly }));
  }, [applyBoardFilters]);

  const toggleBoardFilterTag = useCallback(
    (tag: string) => {
      applyBoardFilters((prev) => {
        const has = prev.tagConjunction.includes(tag);
        const tagConjunction = has
          ? prev.tagConjunction.filter((t) => t !== tag)
          : [...prev.tagConjunction, tag];
        return { ...prev, tagConjunction };
      });
    },
    [applyBoardFilters]
  );

  const setBoardFilterCycle = useCallback(
    (cycle: BoardCycleFilter) => {
      applyBoardFilters((prev) => ({ ...prev, cycle }));
    },
    [applyBoardFilters]
  );

  const clearBoardFilters = useCallback(() => {
    applyBoardFilters(() => ({
      mvpOnly: false,
      dispatchOnly: false,
      tagConjunction: [],
      cycle: 'all',
    }));
  }, [applyBoardFilters]);

  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState('');
  const [roadmapLoading, setRoadmapLoading] = useState(false);
  const [roadmapDataReady, setRoadmapDataReady] = useState(false);

  useEffect(() => {
    if (!roadmapDataReady || filtersUrlHydratedRef.current) return;
    filtersUrlHydratedRef.current = true;
    try {
      const p = new URLSearchParams(window.location.search);
      const mvp = p.get('pbMvp') === '1';
      const dispatch = p.get('pbDispatch') === '1';
      const tagsRaw = p.get('pbTags');
      const tagConjunction =
        tagsRaw && tagsRaw.trim().length > 0
          ? tagsRaw
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : [];
      const cRaw = p.get('pbCycle');
      let cycle: BoardCycleFilter = 'all';
      if (cRaw === 'none') cycle = 'none';
      else if (cRaw != null && cRaw !== '') {
        const n = Number(cRaw);
        if (!Number.isNaN(n)) cycle = n;
      }
      if (mvp || dispatch || tagConjunction.length > 0 || cycle !== 'all') {
        setBoardFiltersState({ mvpOnly: mvp, dispatchOnly: dispatch, tagConjunction, cycle });
      }
    } catch {
      /* ignore */
    }
  }, [roadmapDataReady]);

  const [history, setHistory] = useState<Section[][]>([emptyRoadmapSections]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  /**
   * Operations that can be inversed (undo) or re-applied (redo).
   * Parallel array to `history` — index N is the op that produced history[N].
   * Index 0 is always null (the initial load state has no op to undo).
   */
  type UndoOperation =
    | { type: 'reorder'; column: 'now' | 'next'; previousItems: RoadmapItem[] }
    | { type: 'crossMove'; item: RoadmapItem; fromColumn: string; toColumn: string }
    | null;
  const [undoOps, setUndoOps] = useState<UndoOperation[]>([null]);
  
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const [activeDragId, setActiveDragId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);

  const [lastBoardUpdate, setLastBoardUpdate] = useState<string>('2025-01-06');
  const [blockedItems, setBlockedItems] = useState<{ id: string; item: string; blockedOn: string; owner: string }[]>([]);
  const [sessionNotes, setSessionNotes] = useState<{ date: string; text: string }[]>([
    { date: '2025-01-06', text: 'Product management prototype — Status Board view added.' },
  ]);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteInputText, setNoteInputText] = useState('');
  const [endOfSessionChecks, setEndOfSessionChecks] = useState<Record<string, boolean>>({});

  useEffect(() => {
    saveProductBoardUiToStorage({
      currentView,
      sidePanelView,
      sidePanelBetId,
      sidePanelRoadmapPath,
      filtersExpanded,
    });
  }, [currentView, sidePanelView, sidePanelBetId, sidePanelRoadmapPath, filtersExpanded]);

  useEffect(() => {
    setBoardTitle(null);
  }, [documentId, documentFilePath]);

  const loadRoadmapData = useCallback(() => {
    setRoadmapLoading(true);
    let roadmapUrl = '/api/roadmap';
    if (documentId?.trim()) {
      roadmapUrl += `?documentId=${encodeURIComponent(documentId.trim())}`;
    } else if (documentFilePath?.trim()) {
      roadmapUrl += `?filePath=${encodeURIComponent(documentFilePath.trim())}`;
    }
    const ac = new AbortController();
    const timeoutId = window.setTimeout(() => ac.abort(), 45_000);
    fetchWithAuth(roadmapUrl, { credentials: 'include', signal: ac.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: { boardTitle?: string | null; sections?: Section[]; statusBoard?: { lastBoardUpdate?: string; blocked?: { item: string; blockedOn: string; owner: string }[]; sessionNotes?: { date: string; text: string }[] } }) => {
        setBoardTitle(data.boardTitle ?? null);
        if (data.sections && Array.isArray(data.sections)) {
          const liveSections: Section[] = data.sections.map((s) => ({
            id: s.id,
            title: s.title,
            color: s.color as Section['color'],
            badge: s.id === 'now' ? `Cycle ${mockData.currentCycle.number}` : s.id === 'next' ? 'Shaped & Ready' : s.id === 'later' ? 'Backlog' : 'Completed',
            sourceFilePath: s.sourceFilePath,
            source: s.source,
            statusFilter: s.statusFilter ?? null,
            items: s.items ?? [],
          }));
          setSections(liveSections);
          setHistory([liveSections]);
          setUndoOps([null]);
          setHistoryIndex(0);
        }
        if (data.statusBoard) {
          if (data.statusBoard.lastBoardUpdate) setLastBoardUpdate(data.statusBoard.lastBoardUpdate);
          if (data.statusBoard.blocked) {
            setBlockedItems(data.statusBoard.blocked.map((b, i) => ({ id: `b-${i}`, ...b })));
          }
          if (data.statusBoard.sessionNotes && data.statusBoard.sessionNotes.length > 0) {
            setSessionNotes(data.statusBoard.sessionNotes);
          }
        }
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (ac.signal.aborted) {
          console.warn('[ProductBoard] Roadmap fetch timed out after 45s');
          toast.error('Roadmap load timed out', {
            description: 'API may be overloaded. Try refresh or restart the API server.',
          });
        } else {
          console.warn('[ProductBoard] API unavailable, using mock data:', msg);
        }
        setBoardTitle(null);
        setSections(mockRoadmapSectionsFallback);
        setHistory([mockRoadmapSectionsFallback]);
        setUndoOps([null]);
        setHistoryIndex(0);
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        setRoadmapLoading(false);
        setRoadmapDataReady(true);
      });
  }, [documentId, documentFilePath]);

  /** Raw fetch — call the persist-order API. Throws on failure. Does NOT reload or touch history. */
  const callPersistOrderApi = useCallback(
    async (column: 'now' | 'next', items: RoadmapItem[]) => {
      const orderedPaths = items
        .map((i) => normalizeBetDocFilePath(i.path))
        .filter((p): p is string => Boolean(p));
      if (orderedPaths.length === 0) return;
      const r = await fetchWithAuth('/api/roadmap/persist-order', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ column, orderedPaths }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { details?: string; error?: string };
        throw new Error(j.details || j.error || `HTTP ${r.status}`);
      }
    },
    []
  );

  const persistColumnOrderToApi = useCallback(
    async (column: 'now' | 'next', items: RoadmapItem[]) => {
      try {
        await callPersistOrderApi(column, items);
        // Optimistic update already applied — no loadRoadmapData() needed on success
      } catch (e) {
        console.warn('[ProductBoard] persist-order', e);
        toast.error('Could not save order', { description: String((e as Error).message) });
        loadRoadmapData(); // restore from server truth on error
      }
    },
    [callPersistOrderApi, loadRoadmapData]
  );

  const recordBetCompleteToApi = useCallback(
    async (item: RoadmapItem, fromSectionId: string) => {
      const docPath = normalizeBetDocFilePath(item.path);
      const r = await fetchWithAuth('/api/roadmap/complete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: item.title,
          betPath: docPath || undefined,
          appetite: item.appetite,
          removeFromNow: fromSectionId === 'now',
        }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { details?: string; error?: string };
        throw new Error(j.details || j.error || `HTTP ${r.status}`);
      }
    },
    []
  );

  const roadmapMoveBetBetweenColumnsApi = useCallback(
    async (item: RoadmapItem, fromSectionId: string, toSectionId: string) => {
      const docPath = normalizeBetDocFilePath(item.path);
      const body: Record<string, unknown> = {
        fromColumn: fromSectionId,
        toColumn: toSectionId,
      };
      if (docPath) body.betPath = docPath;
      if (!docPath && fromSectionId === 'later' && item.title) {
        body.itemTitle = item.title;
      }
      if (!body.betPath && !body.itemTitle) {
        throw new Error(
          'This card has no bet file path. Link the bet in the roadmap section, or move from LATER using a matching section title.'
        );
      }
      const r = await fetchWithAuth('/api/roadmap/move', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { details?: string; error?: string };
        throw new Error(j.details || j.error || `HTTP ${r.status}`);
      }
    },
    []
  );

  /** Feedback Board: drag between query-backed columns updates ticket frontmatter status. */
  const patchFeedbackTicketStatusApi = useCallback(
    async (item: RoadmapItem, status: string, promotedTo?: string) => {
      const ticketId = item.documentId || item.id;
      if (!ticketId) throw new Error('Ticket has no document id');
      const body: { status: string; promoted_to?: string } = { status };
      if (promotedTo) body.promoted_to = promotedTo;
      const r = await fetchWithAuth(`/api/feedback-tickets/${encodeURIComponent(ticketId)}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error || `HTTP ${r.status}`);
      }
    },
    []
  );

  useEffect(() => { loadRoadmapData(); }, [loadRoadmapData]);

  useEffect(() => {
    const onToolbarRefresh = () => {
      loadRoadmapData();
      toast.success('Roadmap refreshed');
    };
    window.addEventListener('product-board-refresh', onToolbarRefresh);
    return () => window.removeEventListener('product-board-refresh', onToolbarRefresh);
  }, [loadRoadmapData]);

  // Restore in-flight cloud agent dispatches from DB (frontmatter on handoff docs)
  const restoreActiveDispatches = useCallback(async (currentSections: Section[]) => {
    try {
      const res = await fetchWithAuth('/api/cursor-cloud/active-dispatches');
      if (!res.ok) return;
      const data = await res.json();
      if (!data.dispatches?.length) return;
      setCloudDispatchResult((prev) =>
        mergeActiveDispatchesIntoResult(currentSections, data.dispatches, prev)
      );
    } catch {
      // Non-critical — dispatch badges stay empty until next successful fetch
    }
  }, []);

  useEffect(() => {
    if (!roadmapDataReady) return;
    void restoreActiveDispatches(sections);
  }, [roadmapDataReady, sections, restoreActiveDispatches]);

  const syncCloudDispatchForItem = useCallback(
    (itemId: string, entry: CloudDispatchResultEntry | null) => {
      setCloudDispatchResult((prev) => {
        if (!entry?.agentId) {
          if (!prev[itemId]) return prev;
          const next = { ...prev };
          delete next[itemId];
          return next;
        }
        return { ...prev, [itemId]: entry };
      });
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    const clearPreview = () => {
      setBetPreviewMarkdown(null);
      setBetPreviewLoading(false);
      setBetPreviewError(null);
    };

    if (sidePanelView === 'roadmap-doc-preview' && sidePanelRoadmapPath) {
      setBetPreviewLoading(true);
      setBetPreviewError(null);
      setBetPreviewMarkdown(null);
      fetchWithAuth(
        `/api/documents/by-path?path=${encodeURIComponent(sidePanelRoadmapPath)}&forceReadFromFile=true`,
        { credentials: 'include' }
      )
        .then(async (r) => {
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            throw new Error((err as { message?: string }).message || `Could not load document (${r.status})`);
          }
          return r.json() as Promise<{ content?: string }>;
        })
        .then((data) => {
          if (!cancelled) setBetPreviewMarkdown(data.content ?? '');
        })
        .catch((e: Error) => {
          if (!cancelled) {
            setBetPreviewError(e.message || 'Failed to load roadmap file');
            setBetPreviewMarkdown(null);
          }
        })
        .finally(() => {
          if (!cancelled) setBetPreviewLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    if (sidePanelView === 'bet-preview' && sidePanelBetId) {
      let found: RoadmapItem | undefined;
      for (const section of sections) {
        found = section.items.find((i) => i.id === sidePanelBetId);
        if (found) break;
      }
      if (!found) {
        // Item not yet present in sections (e.g., brief optimistic-update transition or
        // stale sidePanelBetId after a reload). Keep whatever is already showing rather
        // than flashing "Bet not found". The effect will re-run when sections settle.
        setBetPreviewLoading(false);
        setBetPreviewError(null);
        if (!betPreviewMarkdown) {
          setBetPreviewMarkdown(getBetMarkdownMock(sidePanelBetId));
        }
        return undefined;
      }
      const docPath = normalizeBetDocFilePath(found?.path);
      if (!docPath) {
        setBetPreviewLoading(false);
        setBetPreviewError(null);
        setBetPreviewMarkdown(generateItemPreviewMarkdown(found));
        return undefined;
      }
      setBetPreviewLoading(true);
      setBetPreviewError(null);
      setBetPreviewMarkdown(null);
      fetchWithAuth(
        `/api/documents/by-path?path=${encodeURIComponent(docPath)}&forceReadFromFile=true`,
        { credentials: 'include' }
      )
        .then(async (r) => {
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            throw new Error((err as { message?: string }).message || `Could not load document (${r.status})`);
          }
          return r.json() as Promise<{ content?: string }>;
        })
        .then((data) => {
          if (!cancelled) setBetPreviewMarkdown(data.content ?? '');
        })
        .catch((e: Error) => {
          if (!cancelled) {
            setBetPreviewError(e.message || 'Failed to load bet');
            setBetPreviewMarkdown(null);
          }
        })
        .finally(() => {
          if (!cancelled) setBetPreviewLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    clearPreview();
    return undefined;
  }, [sidePanelView, sidePanelBetId, sidePanelRoadmapPath, sections]);

  useEffect(() => {
    const openBet = sidePanelBetId
      ? sections.flatMap(s => s.items).find(i => i.id === sidePanelBetId)
      : null;
    window.dispatchEvent(new CustomEvent('product-board-view-context', {
      detail: {
        view: currentView === 'status-board' ? 'status_board'
            : currentView === 'roadmap' ? 'roadmap'
            : 'dashboard',
        openBetTitle: openBet?.title ?? undefined,
        openBetPath: openBet?.path ?? undefined,
        openRoadmapIndexPath: sidePanelView === 'roadmap-doc-preview' ? sidePanelRoadmapPath ?? undefined : undefined,
      }
    }));
  }, [currentView, sidePanelBetId, sidePanelRoadmapPath, sidePanelView, sections]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const findSectionByItemId = useCallback((itemId: string): Section | undefined => {
    return sections.find(s => s.items.some(i => i.id === itemId));
  }, [sections]);
  
  const activeDragItem = activeDragId 
    ? sections.flatMap(s => s.items).find(i => i.id === activeDragId) 
    : null;
  
  const updateSectionsWithHistory = useCallback((newSections: Section[], animateIds?: string[], op?: UndoOperation) => {
    setSections(newSections);
    setHistory(prev => [...prev.slice(0, historyIndex + 1), newSections]);
    setUndoOps(prev => [...prev.slice(0, historyIndex + 1), op ?? null]);
    setHistoryIndex(prev => prev + 1);
    if (animateIds && animateIds.length > 0) {
      setAnimatingIds(new Set(animateIds));
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      animationTimeoutRef.current = setTimeout(() => {
        setAnimatingIds(new Set());
      }, 500);
    }
  }, [historyIndex]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id);
  }, []);
  
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    setOverId(over?.id ?? null);
  }, []);
  
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveDragId(null);
    setOverId(null);
    
    if (!over) return;
    
    const activeId = active.id as string;
    const overId = over.id as string;
    
    const sourceSection = sections.find(s => s.items.some(i => i.id === activeId));
    if (!sourceSection) return;
    
    let targetSectionId: string | null = null;
    let targetItemIndex: number | null = null;
    
    if (overId.startsWith('section-drop-')) {
      targetSectionId = overId.replace('section-drop-', '');
    } else {
      const overSection = sections.find(s => s.items.some(i => i.id === overId));
      if (overSection) {
        targetSectionId = overSection.id;
        targetItemIndex = overSection.items.findIndex(i => i.id === overId);
      }
    }
    
    if (!targetSectionId) return;
    
    const targetSection = sections.find(s => s.id === targetSectionId);
    if (!targetSection) return;
    
    if (sourceSection.id === targetSection.id) {
      const oldIndex = sourceSection.items.findIndex(i => i.id === activeId);
      const newIndex = targetItemIndex !== null ? targetItemIndex : sourceSection.items.length;

      if (oldIndex !== newIndex) {
        const previousItems = sourceSection.items; // snapshot before move for undo
        const newSections = sections.map(s => {
          if (s.id !== sourceSection.id) return s;
          return { ...s, items: arrayMove(s.items, oldIndex, newIndex) };
        });
        const reorderOp: UndoOperation = (sourceSection.id === 'now' || sourceSection.id === 'next')
          ? { type: 'reorder', column: sourceSection.id as 'now' | 'next', previousItems }
          : null;
        updateSectionsWithHistory(newSections, [activeId], reorderOp);
        scrollBetCardIntoView(activeId);
        toast.success('Reordered item');
        if (sourceSection.id === 'now' || sourceSection.id === 'next') {
          const updated = newSections.find((s) => s.id === sourceSection.id);
          if (updated) {
            void persistColumnOrderToApi(sourceSection.id as 'now' | 'next', updated.items);
          }
        }
      }
    } else {
      const item = sourceSection.items.find((i) => i.id === activeId)!;
      const newSections = sections.map(s => {
        if (s.id === sourceSection.id) {
          return { ...s, items: s.items.filter(i => i.id !== activeId) };
        }
        if (s.id === targetSection.id) {
          const insertIndex = targetItemIndex !== null ? targetItemIndex : s.items.length;
          const newItems = [...s.items];
          newItems.splice(insertIndex, 0, item);
          return { ...s, items: newItems };
        }
        return s;
      });

      const isFeedbackBoardMove =
        sourceSection.source === 'feedback-tickets' &&
        targetSection.source === 'feedback-tickets' &&
        Boolean(targetSection.statusFilter);

      if (isFeedbackBoardMove && targetSection.statusFilter) {
        updateSectionsWithHistory(newSections, [activeId], null);
        scrollBetCardIntoView(activeId);
        void (async () => {
          try {
            await patchFeedbackTicketStatusApi(item, targetSection.statusFilter!);
            toast.success(`Status -> ${targetSection.statusFilter}`);
          } catch (e) {
            toast.error('Could not update ticket status', {
              description: String((e as Error).message),
            });
            loadRoadmapData();
          }
        })();
      } else if (targetSectionId === 'done') {
        // Move to done is not undoable (it writes to DONE.md and requires cycling)
        updateSectionsWithHistory(newSections, [activeId], null);
        scrollBetCardIntoView(activeId);
        void (async () => {
          try {
            await recordBetCompleteToApi(item, sourceSection.id);
            toast.success('Moved to Done and saved to roadmap');
            loadRoadmapData(); // reload to get DONE.md state synced
          } catch (e) {
            toast.error('Could not save completion', { description: String((e as Error).message) });
            loadRoadmapData();
          }
        })();
      } else if (
        ['now', 'next', 'later'].includes(sourceSection.id) &&
        ['now', 'next', 'later'].includes(targetSectionId)
      ) {
        const moveOp: UndoOperation = { type: 'crossMove', item, fromColumn: sourceSection.id, toColumn: targetSectionId };
        updateSectionsWithHistory(newSections, [activeId], moveOp);
        scrollBetCardIntoView(activeId);
        void (async () => {
          try {
            await roadmapMoveBetBetweenColumnsApi(item, sourceSection.id, targetSectionId);
            toast.success(`Moved to ${targetSection.title} — ⌘Z to undo`);
            // No loadRoadmapData() — optimistic update is correct; history preserved for undo
          } catch (e) {
            toast.error('Could not move between columns', { description: String((e as Error).message) });
            loadRoadmapData(); // restore from server truth on error
          }
        })();
      } else {
        updateSectionsWithHistory(newSections, [activeId], null);
        toast.success(`Moved to ${targetSection.title}`);
      }
    }
  }, [sections, updateSectionsWithHistory, persistColumnOrderToApi, recordBetCompleteToApi, roadmapMoveBetBetweenColumnsApi, patchFeedbackTicketStatusApi, loadRoadmapData]);
  
  const undo = useCallback(async () => {
    if (!canUndo) return;
    const op = undoOps[historyIndex]; // op that produced the current state
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    setSections(history[newIndex]);
    toast('Undid last action', { duration: 1500 });

    if (!op) return;
    if (op.type === 'reorder') {
      // Restore previous order on server (previous items are stored in the op)
      await callPersistOrderApi(op.column, op.previousItems).catch((e) => {
        console.warn('[ProductBoard] undo reorder failed:', e);
        loadRoadmapData();
      });
    } else if (op.type === 'crossMove') {
      // Move back: swap fromColumn ↔ toColumn
      try {
        await roadmapMoveBetBetweenColumnsApi(op.item, op.toColumn, op.fromColumn);
      } catch (e) {
        toast.error('Could not undo move', { description: String((e as Error).message) });
        loadRoadmapData();
      }
    }
  }, [canUndo, historyIndex, history, undoOps, callPersistOrderApi, roadmapMoveBetBetweenColumnsApi, loadRoadmapData]);

  const redo = useCallback(async () => {
    if (!canRedo) return;
    const newIndex = historyIndex + 1;
    const op = undoOps[newIndex]; // op that produced the state we're going forward to
    setHistoryIndex(newIndex);
    setSections(history[newIndex]);
    toast('Redid action', { duration: 1500 });

    if (!op) return;
    if (op.type === 'reorder') {
      // Re-apply the forward order (history[newIndex] has the correct items)
      const redoSection = history[newIndex].find(s => s.id === op.column);
      if (redoSection) {
        await callPersistOrderApi(op.column, redoSection.items).catch((e) => {
          console.warn('[ProductBoard] redo reorder failed:', e);
          loadRoadmapData();
        });
      }
    } else if (op.type === 'crossMove') {
      // Re-apply the original move direction
      try {
        await roadmapMoveBetBetweenColumnsApi(op.item, op.fromColumn, op.toColumn);
      } catch (e) {
        toast.error('Could not redo move', { description: String((e as Error).message) });
        loadRoadmapData();
      }
    }
  }, [canRedo, historyIndex, history, undoOps, callPersistOrderApi, roadmapMoveBetBetweenColumnsApi, loadRoadmapData]);
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);
  
  const [addItemSearch, setAddItemSearch] = useState('');
  const [addItemSectionId, setAddItemSectionId] = useState<string | null>(null);
  
  const mockSearchResults = [
    { id: 'cursor-workspace', title: 'Cursor Workspace Integration', type: 'work-spec' },
    { id: 'voice-memo-batch', title: 'Voice Memo Batch Processing', type: 'work-spec' },
    { id: 'keyboard-shortcuts', title: 'Keyboard Shortcuts System', type: 'bet' },
    { id: 'mobile-app', title: 'Mobile App Concept', type: 'idea' },
    { id: 'graph-view', title: 'Graph View Visualization', type: 'bet' },
  ];
  
  const filteredSearchResults = addItemSearch.trim() 
    ? mockSearchResults.filter(r => 
        r.title.toLowerCase().includes(addItemSearch.toLowerCase())
      )
    : mockSearchResults;
  
  const addItemToSection = useCallback((sectionId: string, item: { id: string; title: string; type: string }) => {
    const section = sections.find(s => s.id === sectionId);
    if (section?.items.some(i => i.id === item.id)) {
      toast.error(`"${item.title}" is already in this section`);
      return;
    }
    const newSections = sections.map(s => {
      if (s.id !== sectionId) return s;
      return {
        ...s,
        items: [...s.items, { id: item.id, title: item.title, type: item.type, appetite: '1 week', status: 'needs-shaping' }],
      };
    });
    updateSectionsWithHistory(newSections, [item.id]);
    toast.success(`Added "${item.title}" to section`);
    setAddItemSearch('');
    setAddItemSectionId(null);
  }, [sections, updateSectionsWithHistory]);
  
  const createNewItem = useCallback((sectionId: string, type: 'bet' | 'note') => {
    const newItemId = `new-${type}-${Date.now()}`;
    const newItem: RoadmapItem = {
      id: newItemId,
      title: `New ${type === 'bet' ? 'Bet' : 'Note'}`,
      type: type,
      appetite: type === 'bet' ? '1 week' : undefined,
      status: 'needs-shaping',
    };
    const newSections = sections.map(s => {
      if (s.id !== sectionId) return s;
      return { ...s, items: [...s.items, newItem] };
    });
    updateSectionsWithHistory(newSections, [newItemId]);
    toast.success(`Created new ${type} - click to edit`);
    setAddItemSectionId(null);
  }, [sections, updateSectionsWithHistory]);
  
  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);
  
  const addSection = useCallback(() => {
    const colors: SectionColor[] = ['purple', 'orange', 'red', 'yellow'];
    const newSectionId = `section-${Date.now()}`;
    const newSection: Section = {
      id: newSectionId,
      title: roadmapViewMode === 'rows' ? 'New Row' : 'New Column',
      color: colors[sections.length % colors.length],
      items: [],
    };
    updateSectionsWithHistory([...sections, newSection], [newSectionId]);
    setEditingSectionId(newSection.id);
    setEditingSectionTitle(newSection.title);
    toast.success(`${roadmapViewMode === 'rows' ? 'Row' : 'Column'} created - click title to rename`);
  }, [roadmapViewMode, sections, updateSectionsWithHistory]);
  
  const deleteSection = useCallback((sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (section && section.items.length > 0) {
      toast.error(`Cannot delete "${section.title}" - move items first`);
      return;
    }
    updateSectionsWithHistory(sections.filter(s => s.id !== sectionId));
    toast.success(`${roadmapViewMode === 'rows' ? 'Row' : 'Column'} deleted`);
  }, [sections, roadmapViewMode, updateSectionsWithHistory]);
  
  const renameSection = useCallback((sectionId: string, newTitle: string) => {
    if (!newTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    updateSectionsWithHistory(sections.map(s => 
      s.id === sectionId ? { ...s, title: newTitle.trim() } : s
    ), [sectionId]);
    setEditingSectionId(null);
    setEditingSectionTitle('');
  }, [sections, updateSectionsWithHistory]);

  const changeColumnColor = useCallback((sectionId: string, color: SectionColor) => {
    updateSectionsWithHistory(sections.map(s => s.id === sectionId ? { ...s, color } : s));
  }, [sections, updateSectionsWithHistory]);
  
  const moveSectionLeft = useCallback((sectionId: string) => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (index <= 0) return;
    const newSections = [...sections];
    const swappedId = newSections[index - 1].id;
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    updateSectionsWithHistory(newSections, [sectionId, swappedId]);
  }, [sections, updateSectionsWithHistory]);
  
  const moveSectionRight = useCallback((sectionId: string) => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (index >= sections.length - 1) return;
    const newSections = [...sections];
    const swappedId = newSections[index + 1].id;
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    updateSectionsWithHistory(newSections, [sectionId, swappedId]);
  }, [sections, updateSectionsWithHistory]);
  
  const moveItemToSection = useCallback((itemId: string, fromSectionId: string, toSectionId: string) => {
    const newSections = sections.map(s => ({ ...s, items: [...s.items] }));
    const fromSection = newSections.find(s => s.id === fromSectionId);
    const toSection = newSections.find(s => s.id === toSectionId);
    if (!fromSection || !toSection) return;
    
    const itemIndex = fromSection.items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) return;
    
    const [item] = fromSection.items.splice(itemIndex, 1);
    toSection.items.push(item);
    
    updateSectionsWithHistory(newSections, [itemId]);
    scrollBetCardIntoView(itemId);

    const isFeedbackBoardMove =
      fromSection.source === 'feedback-tickets' &&
      toSection.source === 'feedback-tickets' &&
      Boolean(toSection.statusFilter);

    if (isFeedbackBoardMove && toSection.statusFilter) {
      void (async () => {
        try {
          await patchFeedbackTicketStatusApi(item, toSection.statusFilter!);
          toast.success(`Status -> ${toSection.statusFilter}`);
        } catch (e) {
          toast.error('Could not update ticket status', {
            description: String((e as Error).message),
          });
          loadRoadmapData();
        }
      })();
    } else if (toSectionId === 'done') {
      void (async () => {
        try {
          await recordBetCompleteToApi(item, fromSectionId);
          toast.success('Moved to Done and saved to roadmap');
          loadRoadmapData();
        } catch (e) {
          toast.error('Could not save completion', { description: String((e as Error).message) });
          loadRoadmapData();
        }
      })();
    } else if (['now', 'next', 'later'].includes(fromSectionId) && ['now', 'next', 'later'].includes(toSectionId)) {
      void (async () => {
        try {
          await roadmapMoveBetBetweenColumnsApi(item, fromSectionId, toSectionId);
          toast.success(`Moved to ${toSection.title}`);
          // No loadRoadmapData() — optimistic update is correct; reloading would reassign item IDs
          // and break the side panel preview (sidePanelBetId would no longer match).
        } catch (e) {
          toast.error('Could not move between columns', { description: String((e as Error).message) });
          loadRoadmapData(); // restore from server truth on error
        }
      })();
    } else {
      toast.success(`Moved to ${toSection.title}`);
    }

    if (toSectionId === 'done' && !isFeedbackBoardMove) {
      emitNotification({
        kind: 'activity',
        title: `"${item.title}" completed`,
        body: `Moved from ${fromSection.title} to Done on the Product Board.`,
        source: 'product-board',
      });
      const nextSection = newSections.find(s => s.id === 'next');
      if (nextSection && nextSection.items.length > 0) {
        const nextUp = nextSection.items[0];
        emitNotification({
          kind: 'suggestion',
          title: `"${nextUp.title}" ready to start`,
          body: `"${item.title}" is done — "${nextUp.title}" is next in queue.`,
          source: 'product-board',
          action: {
            label: 'Review in chat',
            chatPrompt: `"${item.title}" just completed on the Product Board. "${nextUp.title}" is next in queue — should we move it to NOW?`,
            contextRefs: nextUp.path ? [{ id: betDocumentReferenceId(nextUp), title: nextUp.title, path: nextUp.path, type: 'bet' }] : undefined,
          },
        });
      }
    } else if (fromSectionId === 'later' && toSectionId === 'next') {
      emitNotification({
        kind: 'activity',
        title: `"${item.title}" added to queue`,
        body: `Moved from Later to Next on the Product Board.`,
        source: 'product-board',
      });
    } else if (fromSectionId !== 'now' && toSectionId === 'now') {
      emitNotification({
        kind: 'activity',
        title: `"${item.title}" started`,
        body: `Moved to NOW — this cycle's active work.`,
        source: 'product-board',
      });
    }
  }, [sections, updateSectionsWithHistory, recordBetCompleteToApi, roadmapMoveBetBetweenColumnsApi, patchFeedbackTicketStatusApi, loadRoadmapData]);

  /** Set promoted_to on a feedback ticket and move to promoted/in_progress status. */
  const promoteFeedbackTicket = useCallback(
    async (item: RoadmapItem, promotedToPath: string) => {
      const path = promotedToPath.trim();
      if (!path) {
        toast.error('promoted_to path required');
        return;
      }
      try {
        await patchFeedbackTicketStatusApi(item, 'in_progress', path);
        toast.success('Linked promoted_to', { description: path });
        loadRoadmapData();
      } catch (e) {
        toast.error('Could not set promoted_to', { description: String((e as Error).message) });
      }
    },
    [patchFeedbackTicketStatusApi, loadRoadmapData]
  );

  const updateItemStatusBoard = useCallback((itemId: string, lastWorked: string, nextSteps: string) => {
    setSections((prev) =>
      prev.map((s) => ({
        ...s,
        items: s.items.map((i) =>
          i.id === itemId ? { ...i, lastWorked: lastWorked || undefined, nextSteps: nextSteps || undefined } : i
        ),
      }))
    );
  }, []);

  const addBlockedItem = useCallback(() => {
    setBlockedItems((prev) => [...prev, { id: `blocked-${Date.now()}`, item: '', blockedOn: '', owner: '' }]);
  }, []);

  const updateBlockedItem = useCallback((id: string, field: 'item' | 'blockedOn' | 'owner', value: string) => {
    setBlockedItems((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
  }, []);

  const removeBlockedItem = useCallback((id: string) => {
    setBlockedItems((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const addSessionNote = useCallback((text: string) => {
    const date = new Date().toISOString().slice(0, 10);
    setSessionNotes((prev) => [{ date, text }, ...prev]);
    setLastBoardUpdate(date);
  }, []);

  const archiveItem = useCallback((itemId: string) => {
    const newSections = sections.map(s => ({
      ...s,
      items: s.items.filter(i => i.id !== itemId),
    }));
    updateSectionsWithHistory(newSections, []);
    toast.success('Archived! Item removed from board.');
  }, [sections, updateSectionsWithHistory]);

  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set([
    'fix-files', 'archive-specs', 'create-index', 'design-system',
    'create-structure', 'create-templates', 'populate-roadmaps',
    'create-guide', 'trial-run'
  ]));

  const handleTaskToggle = useCallback((taskId: string, taskText: string) => {
    const newCompleted = new Set(completedTasks);
    if (newCompleted.has(taskId)) {
      newCompleted.delete(taskId);
      toast(`"${taskText}" marked as incomplete`);
    } else {
      newCompleted.add(taskId);
      toast.success(`✅ "${taskText}" completed!`);
      emitNotification({
        kind: 'activity',
        title: 'Task completed',
        body: `"${taskText}" checked off on Product Board.`,
        source: 'product-board',
      });
    }
    setCompletedTasks(newCompleted);
  }, [completedTasks]);

  const handleOpenDocument = useCallback((path: string, _name: string) => {
    const docPath = normalizeBetDocFilePath(path) ?? path;
    window.dispatchEvent(new CustomEvent('open-document', {
      detail: { filePath: docPath, openInNewTab: false },
    }));
  }, []);

  const handleOpenBetInPanel = useCallback((betId: string) => {
    setSidePanelRoadmapPath(null);
    setSidePanelBetId(betId);
    setSidePanelView('bet-preview');
    toast('Opening bet in side panel', {
      description: 'Preview mode - click "Open" to edit full document',
    });
  }, []);

  const handleOpenRoadmapColumnDocInPanel = useCallback((sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    const docPath = section?.sourceFilePath;
    if (!docPath) return;
    setSidePanelBetId(null);
    setSidePanelRoadmapPath(docPath);
    setSidePanelView('roadmap-doc-preview');
    const human = docPath.replace(/^flownotes-app-docs\//, 'docs/');
    toast('Opening roadmap file in side panel', {
      description: human,
    });
  }, [sections]);

  const handleOpenCycleInPanel = useCallback(() => {
    setSidePanelRoadmapPath(null);
    setSidePanelView('cycle-detail');
    toast('Opening cycle details in side panel', {
      description: 'View and update cycle progress',
    });
  }, []);

  const handleDispatchToCloud = useCallback(async (item: RoadmapItem, targetRepo: 'flownotes' | 'flownotes-content' = 'flownotes') => {
    const targets = resolveCloudDispatchTargets(
      item,
      sidePanelBetId === item.id ? betPreviewMarkdown : null
    );
    const { dispatchPath, dispatchDocumentId, handoffPath, betPath } = targets;
    if (!dispatchPath && !dispatchDocumentId?.trim()) {
      toast.error('Cannot dispatch', { description: 'No handoff or bet file path on this item.' });
      return;
    }
    setCloudDispatchingId(item.id);
    try {
      const data = await dispatchHandoffToCloud({
        handoff_document_path: dispatchPath || undefined,
        handoff_document_id: dispatchDocumentId?.trim() || undefined,
        target_repo: targetRepo,
        organizationId: delegateMcpOrganizationId,
        inboxOrganizationId: delegateMcpOrganizationId,
      });
      if (data.success && data.agentId) {
        const dispatchedAgentId = data.agentId
        setCloudDispatchResult(prev => ({
          ...prev,
          [item.id]: {
            agentId: dispatchedAgentId,
            title: data.title || item.title,
            dispatchedAt: data.dispatchedAt || new Date().toISOString(),
            threadId: data.threadId ?? null,
            dispatchStatus: 'running',
            isActive: true,
          },
        }));
        const via = handoffPath && handoffPath !== betPath ? 'handoff' : 'bet doc';
        toast.success('Cloud agent dispatched', {
          description: `Agent ${data.agentId} is implementing via ${via}: "${data.title || item.title}"`,
        });
      } else {
        setCloudDispatchResult(prev => ({
          ...prev,
          [item.id]: { agentId: '', title: '', dispatchedAt: '', error: data.error },
        }));
        toast.error('Dispatch failed', { description: data.error || 'Unknown error' });
      }
    } catch (err: unknown) {
      toast.error('Dispatch failed', { description: err instanceof Error ? err.message : 'Network error' });
    } finally {
      setCloudDispatchingId(null);
    }
  }, [delegateMcpOrganizationId, sidePanelBetId, betPreviewMarkdown]);

  const handleCloseSidePanel = useCallback(() => {
    setSidePanelView(null);
    setSidePanelBetId(null);
    setSidePanelRoadmapPath(null);
    setBetPreviewMarkdown(null);
    setBetPreviewLoading(false);
    setBetPreviewError(null);
  }, []);

  /** PanelRight toggle: collapse keeps selection; reopen restores or shows empty preview. */
  const handleToggleSidePanel = useCallback(() => {
    if (sidePanelView) {
      setSidePanelView(null);
      return;
    }
    if (sidePanelBetId) {
      setSidePanelView('bet-preview');
      return;
    }
    if (sidePanelRoadmapPath) {
      setSidePanelView('roadmap-doc-preview');
      return;
    }
    setSidePanelView('bet-preview');
  }, [sidePanelView, sidePanelBetId, sidePanelRoadmapPath]);

  const handleOpenInEditor = useCallback(() => {
    if (sidePanelView === 'roadmap-doc-preview' && sidePanelRoadmapPath) {
      window.dispatchEvent(new CustomEvent('open-document', {
        detail: { filePath: sidePanelRoadmapPath, openInNewTab: false },
      }));
      return;
    }

    if (sidePanelView === 'cycle-detail') {
      const cyclePath = normalizeBetDocFilePath('docs/product/cycles/2026-04-cycle-2.md')
        ?? 'flownotes-app-docs/product/cycles/2026-04-cycle-2.md';
      window.dispatchEvent(new CustomEvent('open-document', {
        detail: { filePath: cyclePath, openInNewTab: false },
      }));
      return;
    }

    if (sidePanelBetId) {
      let foundItem: RoadmapItem | undefined;
      for (const section of sections) {
        foundItem = section.items.find(i => i.id === sidePanelBetId);
        if (foundItem) break;
      }

      const docPath = normalizeBetDocFilePath(foundItem?.path);
      if (docPath) {
        window.dispatchEvent(new CustomEvent('open-document', {
          detail: { filePath: docPath, openInNewTab: false },
        }));
      } else {
        const title = foundItem?.title ?? sidePanelBetId;
        toast(`No file path linked for: ${title}`, {
          description: 'Add a path field to this bet item to enable direct navigation.',
        });
      }
      return;
    }

    toast('Nothing to open', { description: 'No item selected.' });
  }, [sidePanelView, sidePanelRoadmapPath, sidePanelBetId, sections]);

  return {
    delegateMcpOrganizationId,
    currentView,
    setCurrentView,
    searchOpen,
    searchQuery,
    setSearchQuery,
    inputRef,
    close: close,
    open: open,
    betSearchDim,
    isSearchActive,
    searchFilteredSections,
    displaySections,
    isBoardFilterActive,
    boardTagFilterEntries,
    boardCycleSelectRows,
    boardFilterMvpCount,
    boardFilterDispatchCount,
    boardFiltersState,
    filtersExpanded,
    setFiltersExpanded,
    toggleBoardFilterMvp,
    toggleBoardFilterDispatch,
    toggleBoardFilterTag,
    setBoardFilterCycle,
    clearBoardFilters,
    selectedBetId,
    setSelectedBetId,
    sidePanelView,
    setSidePanelView,
    sidePanelBetId,
    setSidePanelBetId,
    sidePanelRoadmapPath,
    setSidePanelRoadmapPath,
    betPreviewMarkdown,
    betPreviewLoading,
    betPreviewError,
    cloudDispatchingId,
    cloudDispatchResult,
    syncCloudDispatchForItem,
    roadmapViewMode,
    setRoadmapViewMode,
    collapsedSections,
    sections,
    setSections,
    boardTitle,
    editingSectionId,
    setEditingSectionId,
    editingSectionTitle,
    setEditingSectionTitle,
    roadmapLoading,
    roadmapDataReady,
    canUndo,
    canRedo,
    animatingIds,
    activeDragId,
    overId,
    activeDragItem,
    lastBoardUpdate,
    setLastBoardUpdate,
    blockedItems,
    sessionNotes,
    showNoteInput,
    setShowNoteInput,
    noteInputText,
    setNoteInputText,
    endOfSessionChecks,
    setEndOfSessionChecks,
    loadRoadmapData,
    sensors,
    findSectionByItemId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    undo,
    redo,
    addItemSearch,
    setAddItemSearch,
    addItemSectionId,
    setAddItemSectionId,
    filteredSearchResults,
    addItemToSection,
    createNewItem,
    toggleSection,
    addSection,
    deleteSection,
    renameSection,
    changeColumnColor,
    moveSectionLeft,
    moveSectionRight,
    moveItemToSection,
    promoteFeedbackTicket,
    updateItemStatusBoard,
    addBlockedItem,
    updateBlockedItem,
    removeBlockedItem,
    addSessionNote,
    archiveItem,
    completedTasks,
    handleTaskToggle,
    handleOpenDocument,
    handleOpenBetInPanel,
    handleOpenRoadmapColumnDocInPanel,
    handleOpenCycleInPanel,
    handleDispatchToCloud,
    handleCloseSidePanel,
    handleToggleSidePanel,
    handleOpenInEditor,
  };
}
